import { APP_INITIALIZER, ApplicationConfig, LOCALE_ID, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withHashLocation, withComponentInputBinding } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import localePt from '@angular/common/locales/pt';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';

import { provideEchartsCore } from 'ngx-echarts';
import { provideIcons } from '@ng-icons/core';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { lucideAlertTriangle, lucideArrowLeft, lucideArrowLeftRight, lucideArrowRight, lucideBanknote, lucideBell, lucideBookOpen, lucideBoxes, lucideBuilding2, lucideCalculator, lucideCamera, lucideCheck, lucideCheckCircle, lucideChevronDown, lucideChevronLeft, lucideChevronRight, lucideChevronUp, lucideChevronsUpDown, lucideCircleCheck, lucideCirclePile, lucideCircleSlash, lucideClock, lucideCopy, lucideCreditCard, lucideCrown, lucideDatabase, lucideDownload, lucideEye, lucideEyeOff, lucideFile, lucideFileChartColumn, lucideFileDown, lucideFileText, lucideImage, lucideKey, lucideKeyRound, lucideLandmark, lucideLayoutDashboard, lucideLink, lucideLock, lucideLogIn, lucideLogOut, lucideMail, lucideMapPin, lucideMaximize2, lucideMenu, lucideMessageCircle, lucideMoon, lucideMoreVertical, lucidePackage, lucidePaperclip, lucidePencil, lucidePlus, lucideRefreshCw, lucideRotateCcw, lucideRuler, lucideSave, lucideScanLine, lucideSearch, lucideSend, lucideSettings, lucideShieldUser, lucideSmartphone, lucideSparkles, lucideSprout, lucideStar, lucideSun, lucideTractor, lucideTrash2, lucideTrendingDown, lucideTrendingUp, lucideTruck, lucideUpload, lucideUser, lucideUserCog, lucideUserRound, lucideUsers, lucideWrench, lucideX, lucideXCircle, lucideZoomIn, lucideZoomOut } from '@ng-icons/lucide';
import { FilterService, MessageService } from 'primeng/api';
import { MensagemService } from './core/services/mensagem.service';
import { routes } from './app.routes';

registerLocaleData(localePt);

export const appConfig: ApplicationConfig = {
  providers: [
    provideIcons({ lucideAlertTriangle, lucideArrowLeft, lucideArrowLeftRight, lucideArrowRight, lucideBanknote, lucideBell, lucideBookOpen, lucideBoxes, lucideBuilding2, lucideCalculator, lucideCamera, lucideCheck, lucideCheckCircle, lucideChevronDown, lucideChevronLeft, lucideChevronRight, lucideChevronUp, lucideChevronsUpDown, lucideCircleCheck, lucideCirclePile, lucideCircleSlash, lucideClock, lucideCopy, lucideCreditCard, lucideCrown, lucideDatabase, lucideDownload, lucideEye, lucideEyeOff, lucideFile, lucideFileChartColumn, lucideFileDown, lucideFileText, lucideImage, lucideKey, lucideKeyRound, lucideLandmark, lucideLayoutDashboard, lucideLink, lucideLock, lucideLogIn, lucideLogOut, lucideMail, lucideMapPin, lucideMaximize2, lucideMenu, lucideMessageCircle, lucideMoon, lucideMoreVertical, lucidePackage, lucidePaperclip, lucidePencil, lucidePlus, lucideRefreshCw, lucideRotateCcw, lucideRuler, lucideSave, lucideScanLine, lucideSearch, lucideSend, lucideSettings, lucideShieldUser, lucideSmartphone, lucideSparkles, lucideSprout, lucideStar, lucideSun, lucideTractor, lucideTrash2, lucideTrendingDown, lucideTrendingUp, lucideTruck, lucideUpload, lucideUser, lucideUserCog, lucideUserRound, lucideUsers, lucideWrench, lucideX, lucideXCircle, lucideZoomIn, lucideZoomOut }),
    provideEchartsCore({ echarts: () => import('echarts') }),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
      ripple: true,
      translation: {
        dateFormat: 'dd/mm/yy',
        firstDayOfWeek: 0,
        dayNames: ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'],
        dayNamesShort: ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'],
        dayNamesMin: ['D','S','T','Q','Q','S','S'],
        monthNames: ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'],
        monthNamesShort: ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'],
        today: 'Hoje',
        clear: 'Limpar',
        emptyMessage: 'Nenhum registro encontrado.',
        emptyFilterMessage: 'Nenhum resultado encontrado.',
        emptySearchMessage: 'Nenhum resultado encontrado.',
        emptySelectionMessage: 'Nenhum item selecionado.',
      },
    }),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideAnimationsAsync(),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor, errorInterceptor])),
    provideRouter(routes, withHashLocation(), withComponentInputBinding()),
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    MensagemService,
    { provide: MessageService, useExisting: MensagemService },
    FilterService,
    {
      provide: APP_INITIALIZER,
      useFactory: (fs: FilterService) => () => {
        fs.register('isoDateContains', (value: unknown, filter: unknown): boolean => {
          if (filter === undefined || filter === null || String(filter) === '') return true;
          if (value === undefined || value === null) return false;
          const iso = String(value).slice(0, 10);
          const [y, m, d] = iso.split('-');
          if (!y || !m || !d) return false;
          return `${d}/${m}/${y}`.includes(String(filter));
        });
      },
      deps: [FilterService],
      multi: true,
    },
  ],
};
